import React from "react";
import PropTypes from "prop-types";
import { Image } from "@learningblocks/shared-components";
import { CardModalWindow } from "../SharedComponents/CardModalWindow";
import { CardModalContent } from "../SharedComponents/CardModalContent";

import GlossaryIcon from "@common/images/Glossary.svg";
import MoveIcon from "@common/images/move.svg";
import TrashCan from "@common/images/mini-trash-icon.svg";

import style from "./style.module.scss";

const ENTER_KEY = 13;

export function HexagonCard(props) {
  const [isModalOpen, toggleModal] = React.useState(false);

  const setModalOpen = () => {
    toggleModal(true);
  };

  const setModalClose = () => {
    toggleModal(false);
  };

  const onKeyDown = (callback) => (event) =>
    event.keyCode === ENTER_KEY && callback(event);

  const setClass = (suffix = "") => {
    let styleClass = style["hex" + suffix];
    if (props.className) {
      styleClass = `${styleClass} ${props.className + suffix}`;
    }
    return styleClass;
  };

  const onInfoSelect = () => {
    if (props.openGlossary) props.openGlossary(props.title);
    else setModalOpen();
  };

  const addCardButton = () =>
    props.addCard && (
      <div
        role="button"
        tabIndex={props.tabIndex}
        onKeyDown={onKeyDown(props.addCard)}
        onClick={props.addCard}
        className={style.hex_action}
      >
        <Image src={MoveIcon} alt="move card" />
      </div>
    );

  const removeCardButton = () =>
    props.removeCard && (
      <div
        role="button"
        tabIndex={props.tabIndex}
        onKeyDown={onKeyDown(props.removeCard)}
        onClick={props.removeCard}
        className={style.hex_action}
      >
        <Image src={TrashCan} alt="remove card" />
      </div>
    );

  const renderShadowBox = () =>
    props.shadow && (
      <>
        <div className={style.hex_btm_1}></div>
        <div className={style.hex_btm_2}></div>
        <div className={style.hex_mid_1}></div>
      </>
    );

  return (
    <>
      <div className={setClass()} tabIndex={props.tabIndex}>
        {renderShadowBox()}
        {addCardButton()}
        {removeCardButton()}
        <div className={style.hex_mid_2}></div>
        <div className={style.hex_top_1}></div>
        <div className={style.hex_top_2}></div>
        <div
          role="button"
          className={style.hex_info}
          onClick={onInfoSelect}
          onKeyDown={onKeyDown(onInfoSelect)}
          tabIndex={props.tabIndex}
        >
          <Image src={GlossaryIcon} alt="more-info" />
        </div>
        <div className={setClass("_content")}>
          <Image src={props.image} alt={props.title} />
          <span> {props.title}</span>
        </div>
      </div>
      <CardModalWindow
        isModalOpen={isModalOpen}
        modalTitle={props.modal.title}
        setModalClose={setModalClose}
      >
        <CardModalContent components={props.modal.components} />
      </CardModalWindow>
    </>
  );
}

HexagonCard.propTypes = {
  /**
   * tabIndex to set on component
   */
  tabIndex: PropTypes.string,
  /**
   * ClassName for tag
   */
  className: PropTypes.string,
  /**
   * callback function to be executed when card is added as next step in DesignSection
   * Prop should be null if addCard icon not required
   */
  addCard: PropTypes.func,
  /**
   * callback function to be executed when card needs to be removed from design section tree
   * Prop should be null if removeCard icon not required
   */
  removeCard: PropTypes.func,
  /**
   * Callback function to be executed when info icon in the card is clicked or selected
   * the callback use the title of card to invoke a search operation in glossary
   */
  openGlossary: PropTypes.func,
  /**
   * Url to the small card icon image
   */
  image: PropTypes.string.isRequired,
  /**
   * Title of the card
   */
  title: PropTypes.string.isRequired,
  /**
   * Data to be displayed in the modal
   */
  modal: PropTypes.shape({
    title: PropTypes.string.isRequired,
    components: PropTypes.array.isRequired,
  }).isRequired,
  /**
   * Boolean set to dropshadow
   */
  shadow: PropTypes.bool,
};

HexagonCard.defaultProps = {
  className: "",
  addCard: undefined,
  removeCard: undefined,
  openGlossary: undefined,
  modal: { components: [], title: "" },
  tabIndex: "0",
  shadow: true,
};
